/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: firewallServicesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client for Firewall Services module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  FirewallService,
  FirewallServiceFormData,
  FirewallServicesListResponse,
} from '../types';

/**
 * Fetch firewall services list
 */
export async function fetchFirewallServices(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  protocol?: 'tcp' | 'udp' | 'tcp/udp' | 'icmp' | 'icmpv6' | 'all';
  enabled?: boolean;
}): Promise<FirewallServicesListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.protocol) {
    queryParams.append('protocol', params.protocol);
  }

  if (params.enabled !== undefined) {
    queryParams.append('enabled', params.enabled.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/services?${queryParams}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch firewall services');
  }

  return response.json();
}

/**
 * Fetch firewall service by ID
 */
export async function fetchFirewallServiceById(id: number): Promise<FirewallService> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/services/${id}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch firewall service');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create firewall service
 */
export async function createFirewallService(
  data: FirewallServiceFormData
): Promise<FirewallService> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/services'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create firewall service');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update firewall service
 */
export async function updateFirewallService(
  id: number,
  data: Partial<FirewallServiceFormData>
): Promise<FirewallService> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/services/${id}`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update firewall service');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete firewall service
 */
export async function deleteFirewallService(id: number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/services/${id}`),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete firewall service');
  }
}
